import React, { useMemo, useState, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
  Modal,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Autorenew as AutorenewIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  DoneAll as DoneAllIcon,
  EditNote as EditNoteIcon,
  Paid as PaidIcon,
  Preview as PreviewIcon,
  PublishedWithChanges as PublishedWithChangesIcon,
  ReceiptLong as ReceiptLongIcon,
  WarningAmber as WarningAmberIcon,
  CalendarMonth as CalendarMonthIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useValuations } from '../../../hooks/administration/useValuations';
import { useAuthContext } from '../../../context/AuthProvider';

const STATE_CONFIG = {
  CREADA: {
    label: 'CREADA',
    bg: '#90a4ae',
    color: '#fff',
    description:
      'La valorización existe pero todavía está en una etapa inicial de preparación. Sigue siendo editable.',
    editable: true,
    icon: <EditNoteIcon fontSize="small" />,
  },
  EN_PROCESO: {
    label: 'EN PROCESO',
    bg: '#1976d2',
    color: '#fff',
    description:
      'La valorización se encuentra en elaboración activa. Sigue siendo editable y puede recibir órdenes o ajustes.',
    editable: true,
    icon: <AutorenewIcon fontSize="small" />,
  },
  EN_REVISION: {
    label: 'EN REVISIÓN',
    bg: '#f9a825',
    color: '#000',
    description:
      'La valorización ha pasado a revisión. Ya no debe modificarse directamente hasta aprobarse o devolverse.',
    editable: false,
    icon: <PreviewIcon fontSize="small" />,
  },
  APROBADA: {
    label: 'APROBADA',
    bg: '#2e7d32',
    color: '#fff',
    description:
      'La valorización fue validada y aprobada. Está lista para pasar al flujo de facturación.',
    editable: false,
    icon: <CheckCircleIcon fontSize="small" />,
  },
  FACTURADA: {
    label: 'FACTURADA',
    bg: '#6a1b9a',
    color: '#fff',
    description:
      'La valorización ya fue facturada. Las órdenes vinculadas pasan a estado FACTURADA.',
    editable: false,
    icon: <ReceiptLongIcon fontSize="small" />,
  },
  COBRADA: {
    label: 'COBRADA',
    bg: '#00695c',
    color: '#fff',
    description:
      'La valorización ya fue cobrada. Las órdenes vinculadas pasan a estado CERRADA.',
    editable: false,
    icon: <PaidIcon fontSize="small" />,
  },
  CANCELADA: {
    label: 'CANCELADA',
    bg: '#d32f2f',
    color: '#fff',
    description:
      'La valorización fue cancelada. No debe seguir usándose y sus efectos operativos se revierten si corresponde.',
    editable: false,
    icon: <CancelIcon fontSize="small" />,
  },
};

/*const TRANSITIONS = {
  CREADA: ['EN_PROCESO', 'CANCELADA'],
  EN_PROCESO: ['CREADA', 'EN_REVISION', 'CANCELADA'],
  EN_REVISION: ['EN_PROCESO', 'APROBADA', 'CANCELADA'],
  APROBADA: ['FACTURADA', 'EN_REVISION', 'CANCELADA'],
  FACTURADA: ['COBRADA'],
  COBRADA: [],
  CANCELADA: [],
};*/

const TRANSITIONS = {
  CREADA: ['EN_PROCESO', 'CANCELADA'],
  EN_PROCESO: ['EN_REVISION', 'CANCELADA'],
  EN_REVISION: ['EN_PROCESO', 'APROBADA', 'CANCELADA'],
  APROBADA: ['FACTURADA', 'CANCELADA'],
  FACTURADA: ['COBRADA', 'EN_REVISION'],
  COBRADA: [],
  CANCELADA: [],
};

const formatCurrency = (value, symbol = 'S/') => {
  const number = Number(value || 0);
  return `${symbol} ${number.toFixed(2)}`;
};

const buildTransitionExplanation = (from, to) => {
  const key = `${from}->${to}`;

  const explanations = {
    'CREADA->EN_PROCESO':
      'La valorización seguirá siendo editable, pero pasará a una fase de trabajo activo.',
    'CREADA->CANCELADA':
      'La valorización será cancelada y dejará de ser utilizable.',
    'EN_PROCESO->CREADA':
      'La valorización volverá a estado inicial editable.',
    'EN_PROCESO->EN_REVISION':
      'La valorización se bloqueará para edición y pasará a revisión.',
    'EN_PROCESO->CANCELADA':
      'La valorización será cancelada y se revertirán sus asociaciones si aplica.',
    'EN_REVISION->EN_PROCESO':
      'La valorización volverá a estado editable para correcciones.',
    'EN_REVISION->APROBADA':
      'La valorización quedará aprobada y lista para facturación.',
    'EN_REVISION->CANCELADA':
      'La valorización será cancelada antes de facturarse.',
    'APROBADA->FACTURADA':
      'La valorización se marcará como facturada y las órdenes asociadas pasarán a estado FACTURADA.',
    'APROBADA->EN_REVISION':
      'La valorización volverá a revisión sin quedar facturada.',
    'APROBADA->CANCELADA':
      'La valorización será cancelada antes de facturación.',
    'FACTURADA->COBRADA':
      'La valorización se marcará como cobrada y las órdenes asociadas pasarán a estado CERRADA.',
  };

  return explanations[key] || 'Se realizará una transición de estado sobre la valorización.';
};

const getImpactAlerts = (valuation) => {
  if (!valuation) return [];

  const state = valuation.valcabest;
  const impacts = [];

  if (['CREADA', 'EN_PROCESO'].includes(state)) {
    impacts.push({
      severity: 'info',
      text: 'La valorización es editable. Puedes modificar cabecera, órdenes y ajustes.',
    });
  }

  if (state === 'EN_REVISION') {
    impacts.push({
      severity: 'warning',
      text: 'La valorización está bloqueada para edición. Solo debería aprobarse o volver a EN_PROCESO.',
    });
  }

  if (state === 'APROBADA') {
    impacts.push({
      severity: 'success',
      text: 'La valorización ya fue aprobada. El siguiente paso natural es FACTURADA.',
    });
  }

  if (state === 'FACTURADA') {
    impacts.push({
      severity: 'warning',
      text: 'Las órdenes vinculadas ya deberían estar en estado FACTURADA. Solo queda el paso de COBRADA.',
    });
  }

  if (state === 'COBRADA') {
    impacts.push({
      severity: 'success',
      text: 'La valorización ha completado su ciclo financiero. Las órdenes deben estar en estado CERRADA.',
    });
  }

  if (state === 'CANCELADA') {
    impacts.push({
      severity: 'error',
      text: 'La valorización fue cancelada y ya no debe ser utilizada para flujo operativo.',
    });
  }

  return impacts;
};

const canTransitionByRole = ({
  from,
  to,
  isAdmin,
  isAdministrador,
  isGerencia,
}) => {
  if (!from || !to) return false;

  // Admin: cancelación/eliminación lógica
  if (to === 'CANCELADA') {
    return isAdmin && ['CREADA', 'EN_PROCESO'].includes(from);
  }

  // Administrador
  if (
    isAdministrador &&
    (
      (from === 'CREADA' && to === 'EN_PROCESO') ||
      (from === 'EN_PROCESO' && to === 'EN_REVISION') ||
      (from === 'APROBADA' && to === 'FACTURADA')
    )
  ) {
    return true;
  }

  // Gerencia
  if (
    isGerencia &&
    (
      (from === 'EN_REVISION' && to === 'APROBADA') ||
      (from === 'FACTURADA' && to === 'COBRADA') ||
      (from === 'EN_REVISION' && to === 'EN_PROCESO') ||
      (from === 'FACTURADA' && to === 'EN_REVISION')
    )
  ) {
    return true;
  }

  return false;
};

const ValuationStateActions = ({
  valuation,
  onOpenSummary,
  onOpenPdfData,
}) => {
  const navigate = useNavigate();

  const { user } = useAuthContext();

  const adminID = Number(process.env.REACT_APP_ROL_ADMIN_ID);
  const administradorID = Number(process.env.REACT_APP_ROL_ADMINISTRADOR_ID);
  const gerenciaID = Number(process.env.REACT_APP_ROL_GERENTE_ID);

  const userRoleID = Number(user?.RolID || user?.rolid || user?.RolId);

  const isAdmin = userRoleID === adminID;
  const isAdministrador = userRoleID === administradorID;
  const isGerencia = userRoleID === gerenciaID;

  const {
    changeStateMutation,
    recalculateMutation,
    deleteMutation,
    printValuationPDF,
  } = useValuations();

  const [pdfUrl, setPdfUrl] = useState(null);
  const [openPdfModal, setOpenPdfModal] = useState(false);
  const [openPrintOptions, setOpenPrintOptions] = useState(false);
  const [includeCalendar, setIncludeCalendar] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);

  const [confirmStateOpen, setConfirmStateOpen] = useState(false);
  const [targetState, setTargetState] = useState(null);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const stateInfo = useMemo(() => {
    return STATE_CONFIG[valuation?.valcabest] || {
      label: valuation?.valcabest || 'SIN ESTADO',
      bg: '#bdbdbd',
      color: '#000',
      description: 'Estado no identificado.',
      editable: false,
      icon: <WarningAmberIcon fontSize="small" />,
    };
  }, [valuation]);

  /*const availableTransitions = useMemo(() => {
    if (!valuation?.valcabest) return [];
    return TRANSITIONS[valuation.valcabest] || [];
  }, [valuation]);*/

  const availableTransitions = useMemo(() => {
    const currentState = valuation?.valcabest;
    if (!currentState) return [];

    const transitions = TRANSITIONS[currentState] || [];

    return transitions.filter((nextState) =>
      canTransitionByRole({
        from: currentState,
        to: nextState,
        isAdmin,
        isAdministrador,
        isGerencia,
      })
    );
  }, [valuation, isAdmin, isAdministrador, isGerencia]);

  const impacts = useMemo(() => {
    return getImpactAlerts(valuation);
  }, [valuation]);

  /*const canDelete = useMemo(() => {
    return ['CREADA', 'EN_PROCESO'].includes(valuation?.valcabest);
  }, [valuation]);*/

  const canDelete = useMemo(() => {
    return (
      isAdmin &&
      ['CREADA', 'EN_PROCESO'].includes(valuation?.valcabest)
    );
  }, [valuation, isAdmin]);

  const canEditValuation = useMemo(() => {
    return ['CREADA', 'EN_PROCESO'].includes(valuation?.valcabest);
  }, [valuation]);

  const canPrintPdf = useMemo(() => {
    return (
      Boolean(valuation?.valcabid) &&
      !['CREADA', 'CANCELADA'].includes(valuation?.valcabest)
    );
  }, [valuation]);

  const canRecalculate = useMemo(() => {
    return Boolean(valuation?.valcabid) && valuation?.estreg === 'A';
  }, [valuation]);

  const showAlert = useCallback((message, severity = 'success') => {
    setAlert({
      open: true,
      message,
      severity,
    });
  }, []);

  const handleCloseAlert = () => {
    setAlert((prev) => ({ ...prev, open: false }));
  };

  const handleOpenStateChange = (nextState) => {
    setTargetState(nextState);
    setConfirmStateOpen(true);
  };

  const handleConfirmStateChange = () => {
    if (!valuation?.valcabid || !targetState) return;

    changeStateMutation.mutate(
      {
        id: valuation.valcabid,
        newState: targetState,
      },
      {
        onSuccess: () => {
          showAlert(`Estado actualizado a ${targetState}.`);
          setConfirmStateOpen(false);
          setTargetState(null);
        },
        onError: (error) => {
          showAlert(error?.message || 'Error al cambiar estado.', 'error');
        },
      }
    );
  };

  const handleRecalculate = () => {
    if (!valuation?.valcabid) return;

    recalculateMutation.mutate(valuation.valcabid, {
      onSuccess: () => {
        showAlert('Totales recalculados correctamente.');
      },
      onError: (error) => {
        showAlert(error?.message || 'Error al recalcular.', 'error');
      },
    });
  };

  const handleDelete = () => {
    if (!valuation?.valcabid) return;

    deleteMutation.mutate(valuation.valcabid, {
      onSuccess: () => {
        showAlert('Valorización eliminada correctamente.');
        setConfirmDeleteOpen(false);
        navigate('/administracion/valorizaciones');
      },
      onError: (error) => {
        showAlert(error?.message || 'Error al eliminar valorización.', 'error');
      },
    });
  };

  /*const handleOpenPdf = async () => {
    const url = await printValuationPDF(valuation.valcabid);
    if (url) {
      setPdfUrl(url);
      setOpenPdfModal(true);
    }
  };*/

  const handleOpenPrintOptions = () => {
    if (!canPrintPdf) {
      showAlert(
        'No se puede imprimir el PDF en el estado actual de la valorización.',
        'warning'
      );
      return;
    }

    setIncludeCalendar(false);
    setOpenPrintOptions(true);
  };

  const handleOpenPdf = async () => {
    if (!canPrintPdf) {
      showAlert(
        'No se puede imprimir el PDF en el estado actual de la valorización.',
        'warning'
      );
      return;
    }

    setLoadingPdf(true);

    try {
      const url = await printValuationPDF(valuation.valcabid, {
        includeCalendar,
      });

      if (url) {
        setPdfUrl(url);
        setOpenPdfModal(true);
        setOpenPrintOptions(false);
      } else {
        showAlert('No se pudo generar el PDF.', 'error');
      }
    } catch (error) {
      showAlert(
        error?.response?.data?.error || error?.message || 'Error al abrir el PDF.',
        'error'
      );
    } finally {
      setLoadingPdf(false);
    }
  };

  const handleClosePdfModal = () => {
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
    setOpenPdfModal(false);
  };

  const isAnyMutationRunning =
    changeStateMutation.isPending ||
    recalculateMutation.isPending ||
    deleteMutation.isPending;

  return (
    <>
      <Snackbar
        open={alert.open}
        autoHideDuration={5000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity={alert.severity} onClose={handleCloseAlert}>
          {alert.message}
        </Alert>
      </Snackbar>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          flexDirection={{ xs: 'column', md: 'row' }}
          gap={2}
          mb={2.5}
        >
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Estado y acciones de la valorización
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Controla el ciclo de vida del documento y ejecuta acciones críticas desde aquí.
            </Typography>
          </Box>

          <Chip
            icon={stateInfo.icon}
            label={stateInfo.label}
            sx={{
              backgroundColor: stateInfo.bg,
              color: stateInfo.color,
              fontWeight: 'bold',
              px: 1,
              height: 34,
            }}
          />
        </Box>

        {/* Estado actual */}
        <Paper
          variant="outlined"
          sx={{
            p: 2.2,
            borderRadius: 2.5,
            mb: 2.5,
            backgroundColor: '#fafafa',
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" mb={1}>
            Estado actual
          </Typography>

          <Typography variant="body2" color="text.secondary" mb={2}>
            {stateInfo.description}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">
                Estado actual
              </Typography>
              <Typography fontWeight="bold">{stateInfo.label}</Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">
                Edición permitida
              </Typography>
              <Typography
                fontWeight="bold"
                color={stateInfo.editable ? 'success.main' : 'error.main'}
              >
                {stateInfo.editable ? 'Sí' : 'No'}
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">
                Total actual
              </Typography>
              <Typography fontWeight="bold">
                {formatCurrency(valuation?.valcabtot, valuation?.monsim || 'S/')}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Acciones rápidas */}
        <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 2.5, mb: 2.5 }}>
          <Typography variant="subtitle1" fontWeight="bold" mb={2}>
            Acciones rápidas
          </Typography>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            flexWrap="wrap"
            useFlexGap
          >
            <Tooltip title="Recalcular detalles, ajustes y totales generales">
              <span>
                <Button
                  variant="outlined"
                  startIcon={
                    recalculateMutation.isPending ? (
                      <CircularProgress size={18} />
                    ) : (
                      <PublishedWithChangesIcon />
                    )
                  }
                  onClick={handleRecalculate}
                  disabled={!canRecalculate || isAnyMutationRunning}
                  sx={{ textTransform: 'none' }}
                >
                  {recalculateMutation.isPending ? 'Recalculando...' : 'Recalcular totales'}
                </Button>
              </span>
            </Tooltip>

            <Tooltip title="Abrir resumen económico de la valorización">
              <span>
                <Button
                  variant="outlined"
                  startIcon={<DescriptionIcon />}
                  onClick={onOpenSummary}
                  disabled={!valuation?.valcabid || isAnyMutationRunning}
                  sx={{ textTransform: 'none' }}
                >
                  Ver resumen
                </Button>
              </span>
            </Tooltip>

            {/*<Tooltip title="Abrir datos preparados para PDF">
              <span>
                <Button
                  variant="outlined"
                  startIcon={<ReceiptLongIcon />}
                  onClick={onOpenPdfData}
                  disabled={!valuation?.valcabid || isAnyMutationRunning}
                  sx={{ textTransform: 'none' }}
                >
                  Ver datos PDF
                </Button>
              </span>
            </Tooltip>*/}

            <Tooltip title="Imprimir PDF de la VALORIZACION">
              <span>
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={handleOpenPrintOptions}
                  disabled={!canPrintPdf || isAnyMutationRunning}
                  sx={{ textTransform: 'none', fontWeight: 'bold' }}
                >
                  Preparar PDF
                </Button>
              </span>
            </Tooltip>

            {canDelete && (
              <Tooltip title="Eliminar lógicamente la valorización">
                <span>
                  <Button
                    color="error"
                    variant="outlined"
                    startIcon={
                      deleteMutation.isPending ? (
                        <CircularProgress size={18} />
                      ) : (
                        <DeleteIcon />
                      )
                    }
                    onClick={() => setConfirmDeleteOpen(true)}
                    disabled={isAnyMutationRunning}
                    sx={{ textTransform: 'none' }}
                  >
                    {deleteMutation.isPending ? 'Eliminando...' : 'Eliminar valorización'}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Stack>
        </Paper>

        {/* Transiciones */}
        <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 2.5, mb: 2.5 }}>
          <Typography variant="subtitle1" fontWeight="bold" mb={1}>
            Transiciones disponibles
          </Typography>

          <Typography variant="body2" color="text.secondary" mb={2}>
            Solo se muestran los cambios de estado permitidos desde la situación actual.
          </Typography>

          {availableTransitions.length === 0 ? (
            <Alert severity="info">
              No hay transiciones disponibles desde el estado actual.
            </Alert>
          ) : (
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              flexWrap="wrap"
              useFlexGap
            >
              {availableTransitions.map((nextState) => {
                const nextInfo = STATE_CONFIG[nextState] || {
                  label: nextState,
                  bg: '#bdbdbd',
                  color: '#000',
                };

                return (
                  <Tooltip
                    key={nextState}
                    title={buildTransitionExplanation(valuation?.valcabest, nextState)}
                  >
                    <span>
                      <Button
                        variant="contained"
                        onClick={() => handleOpenStateChange(nextState)}
                        disabled={isAnyMutationRunning}
                        startIcon={
                          changeStateMutation.isPending && targetState === nextState ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            nextInfo.icon || <DoneAllIcon />
                          )
                        }
                        sx={{
                          textTransform: 'none',
                          backgroundColor: nextInfo.bg,
                          color: nextInfo.color,
                          '&:hover': {
                            backgroundColor: nextInfo.bg,
                            opacity: 0.9,
                          },
                        }}
                      >
                        {changeStateMutation.isPending && targetState === nextState
                          ? 'Procesando...'
                          : `Pasar a ${nextInfo.label}`}
                      </Button>
                    </span>
                  </Tooltip>
                );
              })}
            </Stack>
          )}
        </Paper>

        {/* Impactos y reglas */}
        <Paper variant="outlined" sx={{ p: 2.2, borderRadius: 2.5 }}>
          <Typography variant="subtitle1" fontWeight="bold" mb={2}>
            Impacto y reglas del estado
          </Typography>

          <Stack spacing={1.5}>
            {impacts.map((impact, index) => (
              <Alert key={index} severity={impact.severity}>
                {impact.text}
              </Alert>
            ))}

            <Divider />

            <Alert severity="info">
              Al pasar una valorización a <strong>FACTURADA</strong>, las órdenes asociadas pasan a estado <strong>FACTURADA</strong>.
            </Alert>

            <Alert severity="success">
              Al pasar una valorización a <strong>COBRADA</strong>, las órdenes asociadas pasan a estado <strong>CERRADA</strong>.
            </Alert>

            <Alert severity="warning">
              Una valorización solo puede editarse en estado <strong>CREADA</strong> o <strong>EN PROCESO</strong>.
            </Alert>

            <Alert severity="error">
              Solo un usuario administrador del sistema puede cancelar/eliminar una valorización, y únicamente si está en <strong>CREADA</strong> o <strong>EN PROCESO</strong>.
            </Alert>

            {/*<Alert severity="warning">
              Si una valorización editable es <strong>eliminada</strong> o una OS es retirada, las órdenes vuelven a estado <strong>FINALIZADA</strong>.
            </Alert>

            <Alert severity="error">
              Una valorización en estado <strong>FACTURADA</strong> o <strong>COBRADA</strong> no debería ser modificada ni revertida por flujo normal.
            </Alert>*/}
          </Stack>
        </Paper>
      </Paper>

      {/* Confirmación cambio de estado */}
      <Dialog
        open={confirmStateOpen}
        onClose={() => {
          if (!changeStateMutation.isPending) {
            setConfirmStateOpen(false);
            setTargetState(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmar cambio de estado</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Desea cambiar la valorización{' '}
            <strong>
              {valuation ? `VAL-${String(valuation.valcabid).padStart(6, '0')}` : ''}
            </strong>{' '}
            de <strong>{STATE_CONFIG[valuation?.valcabest]?.label || valuation?.valcabest}</strong>{' '}
            a <strong>{STATE_CONFIG[targetState]?.label || targetState}</strong>?
          </DialogContentText>

          <Box mt={2}>
            <Alert severity="info">
              {buildTransitionExplanation(valuation?.valcabest, targetState)}
            </Alert>
          </Box>

          {targetState === 'FACTURADA' && (
            <Box mt={2}>
              <Alert severity="warning">
                Esta acción marcará las órdenes asociadas como <strong>FACTURADA</strong>.
              </Alert>
            </Box>
          )}

          {targetState === 'COBRADA' && (
            <Box mt={2}>
              <Alert severity="success">
                Esta acción marcará las órdenes asociadas como <strong>CERRADA</strong>.
              </Alert>
            </Box>
          )}

          {targetState === 'CANCELADA' && (
            <Box mt={2}>
              <Alert severity="error">
                Esta acción cancelará la valorización. Si aún estaba editable, sus efectos operativos se revertirán.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmStateOpen(false);
              setTargetState(null);
            }}
            disabled={changeStateMutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmStateChange}
            disabled={changeStateMutation.isPending}
          >
            {changeStateMutation.isPending ? 'Procesando...' : 'Confirmar cambio'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmación eliminación */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => {
          if (!deleteMutation.isPending) {
            setConfirmDeleteOpen(false);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmar eliminación de valorización</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Desea eliminar lógicamente la valorización{' '}
            <strong>
              {valuation ? `VAL-${String(valuation.valcabid).padStart(6, '0')}` : ''}
            </strong>
            ?
          </DialogContentText>

          <Box mt={2}>
            <Alert severity="warning">
              Esta acción retirará las OS asociadas si la valorización aún es editable y la marcará como cancelada/eliminada lógicamente.
            </Alert>
          </Box>

          <Box mt={2}>
            <Alert severity="error">
              Esta operación no debe usarse como reemplazo del flujo normal de estados. Úsala solo cuando realmente corresponda eliminar el documento.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDeleteOpen(false)}
            disabled={deleteMutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Eliminando...' : 'Eliminar valorización'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openPrintOptions}
        onClose={() => {
          if (!loadingPdf) setOpenPrintOptions(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <PictureAsPdfIcon color="warning" />
            <Typography variant="h6" fontWeight="bold">
              Preparar PDF de valorización
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ backgroundColor: '#fafafa' }}>
          <Stack spacing={2}>
            <Alert severity="info">
              Selecciona cómo deseas generar el PDF para el cliente.
            </Alert>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CalendarMonthIcon color={includeCalendar ? 'primary' : 'disabled'} />

                <Box flex={1}>
                  <Typography fontWeight="bold">
                    Incluir anexo de calendario
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Agrega después de las firmas un calendario de servicios por ítem.
                    Si el ítem está basado en manifiestos, contará manifiestos completados por día.
                  </Typography>
                </Box>

                <FormControlLabel
                  control={
                    <Switch
                      checked={includeCalendar}
                      onChange={(e) => setIncludeCalendar(e.target.checked)}
                      disabled={loadingPdf}
                    />
                  }
                  label={includeCalendar ? 'Sí' : 'No'}
                  labelPlacement="start"
                />
              </Stack>
            </Paper>

            <Alert severity={includeCalendar ? 'success' : 'warning'} variant="outlined">
              {includeCalendar
                ? 'El PDF incluirá el anexo de calendario.'
                : 'El PDF se generará solo con valorización, totales y firmas.'}
            </Alert>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setOpenPrintOptions(false)}
            disabled={loadingPdf}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            color="warning"
            startIcon={
              loadingPdf ? <CircularProgress size={18} color="inherit" /> : <PictureAsPdfIcon />
            }
            onClick={handleOpenPdf}
            disabled={loadingPdf}
            sx={{ textTransform: 'none', fontWeight: 'bold' }}
          >
            {loadingPdf ? 'Generando PDF...' : 'Generar y previsualizar'}
          </Button>
        </DialogActions>
      </Dialog>


      <Modal open={openPdfModal} onClose={handleClosePdfModal}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '96%', md: '82%' },
            height: { xs: '90%', md: '88%' },
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 3,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 1.5,
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#fafafa',
            }}
          >
            <Box>
              <Typography fontWeight="bold">
                PDF de valorización
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {includeCalendar
                  ? 'Incluye anexo de calendario'
                  : 'Sin anexo de calendario'}
              </Typography>
            </Box>

            <Button onClick={handleClosePdfModal}>
              Cerrar
            </Button>
          </Box>

          <Box flex={1}>
            {pdfUrl ? (
              <iframe
                src={pdfUrl}
                title="Valorización PDF"
                width="100%"
                height="100%"
                style={{ border: 'none' }}
              />
            ) : (
              <Box
                height="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Typography variant="body2" color="text.secondary">
                  Cargando PDF o URL inválida.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default ValuationStateActions;